import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  viewChild,
  inject,
  OnInit,
  signal,
  Signal,
} from '@angular/core';

import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { catchError, filter, map, merge, Observable, of, switchMap, tap } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxMaskDirective } from 'ngx-mask';
import { GetUserService } from '../services/get-user.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GetCountriesService } from '../../services/get-countries.service';
import { fullNameValidator } from './custom-validator/full-name.validator';
import { emailValidator } from './custom-validator/email.validator';
import { shippingAddressValidator } from './custom-validator/shipping-address.validator';
import { cityValidator } from './custom-validator/city.validator';
import { stateValidator } from './custom-validator/state.validator';
import { CountryFormatService } from '../services/country-format.service';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { ConfirmModalComponent } from '@app/components/shared/confirm-modal/confirm-modal.component';
import { CountriesList, Country } from '@app/utils/interfaces/countries-list';
import { UserDataFormGroup, UserDataResponse, UserData } from '@app/utils/interfaces/user-data';
import {
  DEFAULT_PHONE_MASK,
  DEFAULT_ZIP_MASK,
  DEFAULT_COUNTRY,
} from '@app/utils/constants/countries.constants';
import { COUNTRY_NAME_TO_CODE_MAP } from '@app/utils/constants/country-mapping.constants';
import { LocalStorageKeys } from '@app/utils/enums/local-storage';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { CountriesEnumList } from '@app/utils/enums/countries.enum';
import { HttpStatusCode } from '@app/utils/enums/http-status-codes.enum';
import { StatusModalComponent } from '@app/components/shared/status-modal/status-modal.component';
import { USER_PROFILE_OPTIONS } from '@app/utils/constants/user-profile.constants';

@Component({
  selector: 'app-user-profile-general',
  standalone: true,
  imports: [
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDividerModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    UserAvatarComponent,
  ],
  templateUrl: './user-profile-general.component.html',
  styleUrl: './user-profile-general.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileGeneralComponent implements OnInit {
  private readonly fileInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required('fileInput');
  private readonly countryFormatService = inject(CountryFormatService);
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(GetUserService);
  private readonly userStateService = inject(UserStateService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly countryService = inject(GetCountriesService);

  public form!: UserDataFormGroup;
  public countries = signal<CountriesList>({ data: [] });
  public userData: UserDataResponse | null = null;
  public dataSaved = signal(false);
  public CountriesName = CountriesEnumList;

  public phoneMask = DEFAULT_PHONE_MASK;
  public zipMask = DEFAULT_ZIP_MASK;
  public phoneReg: RegExp | null = null;
  public zipReg: RegExp | null = null;
  public userAvatar = this.userStateService.photoUrl;
  public phonePrefix = signal('');
  public zipPrefix = signal('');
  public isPhoneFocused = signal(false);
  public countryNameToCodeMap = COUNTRY_NAME_TO_CODE_MAP;

  private readonly loader = toSignal(
    this.userStateService.updateUserState().pipe(switchMap(() => this.getCountriesData())),
    { initialValue: null },
  );

  public ngOnInit(): void {
    this.form = this.createForm();
    this.loader();
    this.setupCountrySync();
  }

  public get hasPhoneError(): boolean {
    const control = this.form.controls.phoneNumber;
    return control.invalid && (control.dirty || control.touched);
  }

  public onPhoneFocus(): void {
    this.isPhoneFocused.set(true);
  }

  public onPhoneBlur(): void {
    this.isPhoneFocused.set(false);
  }

  public getPhoneCode(): string {
    const phoneCountryName = this.form.controls.phoneCountryId.value;

    const country = this.findCountryByName(phoneCountryName);
    return country?.phoneCode ?? '';
  }

  public getCountryCode(): string {
    const phoneCountryName = this.form.controls.phoneCountryId.value;
    return this.countryNameToCodeMap[phoneCountryName] || '';
  }

  public onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const id = this.getCurrentStaffId();
    if (!id) {
      return;
    }

    const country = this.findCountryByName(this.form.controls.countryId.value);
    if (!country) return;

    const phoneValue = this.countryFormatService.formatPhoneForSubmit(
      this.form.controls.phoneNumber.value,
      country.name,
      country.phoneMask,
    );

    const zipValue = this.countryFormatService.formatZipForSubmit(
      this.form.controls.zipCode.value,
      country.name,
      country.zipCodeMask,
    );

    const formDataSend = {
      firstName: this.getFormControl('firstName').value,
      lastName: this.getFormControl('lastName').value,
      email: this.getFormControl('email').value,
      shippingAddress: this.getFormControl('shippingAddress').value,
      city: this.getFormControl('city').value,
      state: this.getFormControl('state').value,
      countryId: country.id.toString(),
      phoneCountryId: country.id.toString(),
      phoneNumber: phoneValue ?? '',
      zipCode: zipValue ?? '',
    };

    this.userService
      .putUser(id, formDataSend)
      .pipe(
        catchError((error) => {
          if (error.status === HttpStatusCode.CONFLICTS) {
            this.openErrorModal(error?.error.message);
          } else {
            this.openErrorModal();
          }

          return of(null);
        }),
      )
      .subscribe(() => {
        if (this.userData) {
          this.userData = {
            ...this.userData,
            data: {
              ...this.userData.data,
              ...formDataSend,
            },
          };
        }

        this.userStateService.applyAvatarChanges();
        this.dataSaved.set(true);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      });
  }

  public cancelDataChange(): void {
    if (this.form.dirty) {
      const dialogRef = this.dialog.open(ConfirmModalComponent, {
        ...USER_PROFILE_OPTIONS,
        data: { message: 'user-profile.general.buttonsInteraction.cancelConformation' },
      });

      dialogRef
        .afterClosed()
        .pipe(filter((confirmed: boolean) => confirmed))
        .subscribe(() => {
          if (this.userData) {
            this.setUserData(this.userData);
          } else {
            this.form.reset();
          }
          this.userStateService.resetAvatarChanges();
          this.form.markAsUntouched();
          this.form.markAsPristine();
        });
    }
  }

  public getFormControl<K extends keyof UserData>(key: K): FormControl<UserData[K]> {
    return this.form.controls[key];
  }

  private createForm(): UserDataFormGroup {
    return this.fb.nonNullable.group({
      firstName: this.fb.nonNullable.control('', [Validators.required, fullNameValidator()]),
      lastName: this.fb.nonNullable.control('', [Validators.required, fullNameValidator()]),
      email: this.fb.nonNullable.control('', [Validators.required, emailValidator()]),
      phoneCountryId: this.fb.nonNullable.control('', Validators.required),
      phoneNumber: this.fb.nonNullable.control('', [Validators.required]),
      shippingAddress: this.fb.nonNullable.control('', [
        Validators.required,
        shippingAddressValidator(),
      ]),
      countryId: this.fb.nonNullable.control('', Validators.required),
      city: this.fb.nonNullable.control('', [Validators.required, cityValidator()]),
      state: this.fb.nonNullable.control('', stateValidator()),
      zipCode: this.fb.nonNullable.control('', [Validators.required]),
    });
  }

  private getCountriesData(): Observable<CountriesList> {
    return this.countryService.getCountries().pipe(
      tap((data: CountriesList) => {
        this.countries.set(data);

        this.form.controls.countryId.setValue(DEFAULT_COUNTRY);
        this.form.controls.phoneCountryId.setValue(DEFAULT_COUNTRY);

        this.getUserData();
      }),
      catchError(() => {
        this.openErrorModal();
        return of([] as unknown as CountriesList);
      }),
    );
  }

  private updateConfiguration(
    value: string,
    control: AbstractControl,
    primaryConfigUpdate: (value: string) => void,
    secondaryConfigUpdate: (value: string) => void,
  ): void {
    primaryConfigUpdate(value);

    if (control.value !== value) {
      control.setValue(value, { emitEvent: false });
      secondaryConfigUpdate(value);
    }
  }

  private setupCountrySync(): void {
    const countryIdControl = this.form.controls.countryId;
    const phoneCountryIdControl = this.form.controls.phoneCountryId;

    merge(
      countryIdControl.valueChanges.pipe(map((value) => ({ source: 'country', value }))),
      phoneCountryIdControl.valueChanges.pipe(map((value) => ({ source: 'phone', value }))),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ source, value }) => {
        if (source === 'country') {
          this.updateConfiguration(
            value,
            phoneCountryIdControl,
            this.updateZipConfiguration.bind(this),
            this.updatePhoneConfiguration.bind(this),
          );
        } else {
          this.updateConfiguration(
            value,
            countryIdControl,
            this.updatePhoneConfiguration.bind(this),
            this.updateZipConfiguration.bind(this),
          );
        }
      });
  }

  private updatePhoneConfiguration(countryName: string): void {
    const selectedCountry = this.findCountryByName(countryName);
    this.phoneReg = selectedCountry?.phoneMask ? new RegExp(selectedCountry.phoneMask) : null;

    const phoneControl = this.form.controls.phoneNumber;
    const config = this.countryFormatService.configurePhoneControl(
      phoneControl,
      countryName,
      this.phoneReg,
    );

    this.phoneMask = config.mask;
    this.phonePrefix.set(config.prefix);

    if (config.prefix && (!phoneControl.value || phoneControl.value === config.prefix)) {
      phoneControl.setValue('');
    }

    const currentValue = phoneControl.value ?? '';
    phoneControl.patchValue(currentValue);
    phoneControl.updateValueAndValidity();
  }

  private updateZipConfiguration(countryName: string): void {
    const selectedCountry = this.findCountryByName(countryName);
    this.zipReg = selectedCountry?.zipCodeMask ? new RegExp(selectedCountry.zipCodeMask) : null;

    const zipControl = this.form.controls.zipCode;
    const config = this.countryFormatService.configureZipControl(
      zipControl,
      countryName,
      this.zipReg,
    );

    this.zipMask = config.mask;
    this.zipPrefix.set(config.prefix);

    const currentValue = zipControl.value ?? '';
    zipControl.patchValue(currentValue);
    zipControl.updateValueAndValidity();
  }

  private findCountryByName(countryName: string): Country | undefined {
    return this.countries().data.find((country) => country.name === countryName);
  }

  private findCountryById(countryId: string | number): Country | undefined {
    return this.countries().data.find((country) => String(country.id) === String(countryId));
  }

  private getUserData(): void {
    const id: string | null = localStorage.getItem(LocalStorageKeys.STAFF_ID);

    if (!id) {
      return;
    }

    const storedUserData = this.userStateService.userData();

    if (!storedUserData) {
      return;
    }

    this.userData = storedUserData;
    this.setUserData(storedUserData);

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private getCurrentStaffId(): string | null {
    const storedUserData = this.userStateService.userData();

    return storedUserData?.data?.id ?? null;
  }

  private openErrorModal(message?: string): void {
    this.dialog.open(StatusModalComponent, {
      data: {
        isSuccess: false,
        title: 'user-profile.general.fetchErrorTitle',
        description: message || 'user-profile.general.fetchErrorMessage',
      },
    });
  }

  private setUserData({ data }: UserDataResponse): void {
    this.form.patchValue(data);

    if (data.countryId) {
      const country = this.findCountryById(data.countryId);
      if (country) {
        this.form.controls.countryId.setValue(country.name);
        this.form.controls.phoneCountryId.setValue(country.name);

        if (data.phoneNumber) {
          const parsedPhone = this.countryFormatService.parsePhoneFromServer(
            data.phoneNumber,
            country.name,
          );
          this.form.controls.phoneNumber.setValue(parsedPhone);
        }
      }
    } else {
      const currentCountry = this.form.controls.countryId.value;
      this.form.controls.phoneCountryId.setValue(currentCountry);
    }

    if (data.zipCode) {
      const currentCountry = this.form.controls.countryId.value;
      const parsedZip = this.countryFormatService.parseZipFromServer(data.zipCode, currentCountry);
      this.form.controls.zipCode.setValue(parsedZip);
    }

    this.userAvatar = this.userStateService.photoUrl;
  }
}
